import {environment} from '../../../environments/environment';
import { DiscountType, TABLE_NUMBER_TYPE } from '../enums/util.enum';
import {ImageConfig, SERVICE_PROVIDER} from '../enums/merchant.enum';
import { Category } from './catalog.model';

export enum RestaurantStatus {
  ACTIVE = 'ACTIVE'
}

export enum RangeParam {
  RANGE_START_TIME = 'RANGE_START_TIME',
  RANGE_END_TIME = 'RANGE_END_TIME'
}

export enum Currency {
  USD = 'USD',
  CAD = 'CAD',
  GBP = 'GBP',
  AUD = 'AUD',
  JPY = 'JPY'
}

export enum ItemFilter {
  CONTAINS_ALCOHOL = 'CONTAINS_ALCOHOL'
}

export const dayOfWeek: string[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY'
];

export class Restaurant {
  constructor(
    public locationId: string = null,
    public readonly merchantId: string = null,
    public name: string = null,
    public logoUrl: string = null,
    public locationImageUrl: string = null,
    public locationBanner: string = './assets/images/back.jpg',
    public locationHeaderTitle: string = null,
    public locationHeaderText: string = null,
    public brandColor: string = null,
    public secondaryBrandColor: string = null,
    public backgroundColor: string = null,
    public secondaryBackgroundColor: string = null,
    public borderColor: string = null,
    public primaryTextColor: string = null,
    public secondaryTextColor: string = null,
    public warningTextColor: string = null,
    public customCSS: string = null,
    public isMobileEnabled: boolean = true,
    public isTippingEnabled: boolean = false,
    public isTabsEnabled: boolean = false,
    public isLoyaltyEnabled: boolean = false,
    public loyaltyPointsName: any = [],
    public isSharedCardsEnabled: boolean = false,
    public allowScheduledPickups: boolean = false,
    public authAmountInCents: number = 0,
    public isShowHours: boolean = false,
    public disableASAP: boolean = false,
    public selectAsapByDefault: boolean = false,
    public disableQuickCheckout: boolean = false,
    public showTaxesAndFeesBreakout: boolean = false,
    public enforceInventory: boolean = false,
    public hideOrdersAhead: boolean = false,
    public showAgeGate: boolean = false,
    public showAllLocationsButton: boolean = true,
    public printAheadDuration: string = null,
    public pickupWindowDuration: string = null,
    public pickupDaysInAdvance: string = null,
    public categoriesCollapsable: boolean = false,
    public requireBillingAddress: boolean = false,
    public requireEmail: boolean = false,
    public enableAppleGooglePay: boolean = false,
    public enableGiftCards: boolean = false,
    public enableStrictTabLanguage: boolean = false,
    public enablePromoCodes: boolean = false,
    public handoffLocationNumbered: boolean = false,
    public hideAllSigns: boolean = false,
    public disableTodayOrders: boolean = false,
    public showAllTimeRestrictedItems: boolean = false,
    public showAllFulfillmentRestrictedItems: boolean = false,
    public showPromoCodeField: boolean = false,
    status: RestaurantStatus | string = RestaurantStatus.ACTIVE,
    public minimumCurbsideAmountInCents: number = 0,
    public minimumHandoffAmountInCents: number = 0,
    public minimumPickupAmountInCents: number = 0,
    public minimumTablesideAmountInCents: number = 0,
    public enlargeProductImages: boolean = false,
    public cancellationDeadlineDuration: string = null,
    public availableStatus: RestaurantAvailableStatus = new RestaurantAvailableStatus(),
    public blackoutDateRanges: BlackoutDay[] = [],
    public country: string = 'US',
    public address = new RestaurantAddress(),
    public currency: string = 'USD',
    public hoursOfOperation: WorkDay[] = [],
    public tipAmounts: number[] = [],
    public tipType: string = null,
    public timezone: string = null,
    public isAcceptingOrders?: boolean,
    public readonly nextOpenTimeUtc?: string,
    public readonly estimatedTimeToBeginPreparation?: string,
    public readonly hideETA?: boolean,
    public numPersonsAhead?: number,
    public readonly maxOrdersInQueue?: number,
    public readonly placeInLine?: number,
    public readonly estimatedPrepTimeDuration?: string,
    public readonly addlDeliveryPrepTimeDuration?: string,
    public readonly person_name?: string,
    public readonly position?: string,
    public readonly description?: string,
    public readonly contactPhoneNumber?: string,
    public readonly kyooFee?: number,
    public readonly autoGratuityPercent?: number,
    public readonly closeInactiveTabsAfterMinutes?: number,
    public newLoyaltyPointsName?: any,
    public autoSelectFulfillment?: boolean,
    public showWindowEndTime?: boolean,
    public serviceChargeName: string = null,
    public gift_cards_permissions_requested: boolean = false,
    public select_soonest_available_by_default: boolean = false,
    public orderConfirmationText: string = null,
    public distance: number = null,
    public enableRestaurantTip: boolean = false,
    public defaultDeliveryAutoGratuityPercent: number = 0,
    public minMaxRestrictions: RestaurantMinMaxOrder = new RestaurantMinMaxOrder(),
    public enableSoldOutOrders: boolean = false,
    public isOrderThrottling: boolean = false,
    public isShowAddons: boolean = false,
    public showSellerDeliveryLocations: boolean = false,
    public sellerDeliveryLocations: string[] = [],
    public sellerDispatchAddress: RestaurantAddress =  new RestaurantAddress(),
    public courierPoweredDeliveryEnabled: boolean = false,
    public sellerPoweredDeliveryEnabled: boolean =  false,
    public isTablesideNumberRequired: boolean = false,
    public enableSharedOrders: boolean = false,
    public enableSharedOrderSingleOrderReceivedSms: boolean = false,
    public displayGiftCardBalance: boolean = false,
    public allowEditTableNumber: boolean = true,
    public displayKyooFeeOnMenu: boolean = false,
    public requirePhoneVerification: boolean = false,
    public collapseModifierAndVariation: boolean = false,
    public hidePickupTime: boolean = false,
    public enable3dSecure: boolean = false,
    public enableParentSubCategories: boolean = false,
    public enforceAcceptOrderThrottling: boolean = false,
    public enableFavoriteItems: boolean = false,
    public enableIkonPass: boolean = false,
    public hideSoldOutItems: boolean = false,
    public showSoldOutItemsLast: boolean = false,
    public hideItemsOutsideDailyAvailability: boolean = false,
    public showNotCurrentlySoldCategory: boolean = false,
    public qrOrderOnly: boolean = false,
    public qrCodeOrderingOnlyErrorMessage: string = null,
    public atriumEnabled: boolean = false,
    public atriumConnectUrl: string = null,
    public showHoursInfo: boolean = false,
    public disableForPickupAt: boolean = false,
    public showDietaryIcons: boolean = false,
    public showDietaryFilter: boolean = false,
    public autoCollapseCategories: number = 0,
    public displayItemImagesOnMenu: boolean = true,
    public applyPrepTimeBeforeOpening: boolean = false,
    public hideAvailabilityMessages: boolean = false,
    public showQuickAddButton: boolean = true,
    public tableNumberType: TABLE_NUMBER_TYPE = TABLE_NUMBER_TYPE.alphanumeric,
    public scheduleTimeBeforeMenu: boolean = false,
    public contact_channel: ContactChannel = new ContactChannel(),
    public contact_phones: string[] = [],
    public contact_emails: string[] = [],
    public display_contact_phones: boolean = false,
    public display_contact_emails: boolean = false,
    public useFlavorShiftsTime: boolean = false,
    public absorb_kyoo_fee: boolean = false,
    public service_provider: SERVICE_PROVIDER = SERVICE_PROVIDER.square,
    public categories: Category[] = [],
  ) {
    this.status = status;
  }

  public _status: RestaurantStatus;

  public get status(): RestaurantStatus | string {
    return this._status;
  }

  public set status(status: RestaurantStatus | string) {
    this._status = RestaurantStatus[status] || RestaurantStatus.ACTIVE;
  }
}

export class ContactChannel {
  constructor(
    public facebook_url: string = null,
    public instagram_url: string = null,
    public linkedin_url: string = null,
    public tiktok_url: string = null,
    public x_url: string = null,
    public youtube_url: string = null,
  ) {
  }
}

export class RestaurantMinMaxOrder {
  constructor(
    public pickup: OrderRestriction = new OrderRestriction(),
    public curbside: OrderRestriction = new OrderRestriction(),
    public tableside: OrderRestriction = new OrderRestriction(),
    public tab: OrderRestriction = new OrderRestriction(),
    public delivery: OrderRestriction = new OrderRestriction(),
    public handoff: OrderRestriction = new OrderRestriction()
  ) {
  }
}

export class OrderRestriction {
  constructor(
    public minimum: number = 0,
    public maximum: number = 0
  ) {
  }
}

export class RestaurantAddress {
  constructor(
    public country: string = null,
    public addressLine: string = null,
    public locality: string = null,
    public state: string = null,
    public postalCode: string = null,
    public coordinates: Coordinate = null,
  ) {
  }
}

export class Price {
  public currency: Currency;
  public amount: number;

  constructor(
    amount: number = 0,
    currency: Currency = Currency.USD
  ) {
    this.amount = amount;
    this.currency = currency;
  }

  public get getAmount(): number {
    return this.amount;
  }

  public set setAmount(amount: number) {
    this.amount = amount;
  }

  public get formattedAmount(): number {
    if ([Currency.USD, Currency.CAD, Currency.GBP, Currency.AUD].includes(this.currency)) {
      return this.amount / 100;
    }
    if (this.currency === Currency.JPY) {
      return this.amount;
    }
    return 0;
  }

  public get getCurrency(): RestaurantStatus | string {
    return this.currency;
  }

  public set setCurrency(currency: RestaurantStatus | string) {
    this.currency = Currency[currency] || Currency.USD;
  }
}

export class NutritionFacts {
  constructor(
    public added_sugars: number = 0,
    public calories: number = 0,
    public cholesterol: number = 0,
    public fiber: number = 0,
    public potassium: number = 0,
    public protein: number = 0,
    public saturated_fat: number = 0,
    public sodium: number = 0,
    public sugars: number = 0,
    public total_carbs: number = 0,
    public total_fat: number = 0,
    public trans_fat: number = 0
  ) {
  }
}
export class DietaryLabels {
  constructor(
    public dairy_free: boolean = false,
    public gluten_free: boolean = false,
    public halal: boolean = false,
    public vegan: boolean = false,
    public vegetarian: boolean = false
  ) {
  }
}

export class DayType {
  constructor(public dayType: string = null) {
  }

  public get day(): number {
    return dayOfWeek.indexOf(this.dayType);
  }

}

export class Time {
  constructor(
    public hour: number = null,
    public minute: number = null
  ) {
  }
}

export class WorkTime {
  constructor(
    public time: Time = new Time(),
    public meridian: boolean = false
  ) {
  }

  public get today(): Date {
    return this.getDate(new Date());
  }

  public getDate(initialDate: Date): Date {
    const date = new Date(initialDate);
    let {hour} = this.time;
    const {minute} = this.time;
    if (hour === 12) {
      hour = 0;
    }
    if (!this.meridian) {
      hour += 12;
    }
    date.setHours(hour, minute);
    return date;
  }
}

export class BlackoutDay {
  constructor(
    public readonly id: string = null,
    public locationId: string = null,
    public enable: boolean = false,
    public name: string = null,
    public startDate: Date = new Date(),
    public endDate: Date = new Date()
  ) {
  }
}

export class BlackoutRange {
  constructor(
    public readonly id: string = null,
    public locationId: string = null,
    public name: string = null,
    public dates: Date[] = [],
    public times: WorkTime[] = []
  ) {
  }
}

export class WorkDay {
  constructor(
    public readonly id: string = null,
    public locationId: string = null,
    public enable: boolean = false,
    public dayType: DayType = new DayType(),
    public startTime: WorkTime[] = [],
    public endTime: WorkTime[] = [],
    public isBlackouted: boolean = false,
    public blackoutDayName: string = null,
    public date: Date = null
  ) {
  }
}

export class Taxes {
  constructor(
    public id: string = null,
    public percentage: number = null,
    public kyooFee: number = null,
    public autoGratuity: number = null,
    public isInclusiveTax: boolean = false
  ) {
  }
}

export class Discount {
  constructor(
    public uid: string = null,
    public autoGratuity: number = null,
    public isInclusiveTax: boolean = false,
    public type: DiscountType = DiscountType.discount,
    public scope: string = null,
    public percentage: string = null,
    public amount_money: Price = null,
    public applied_money: Price = null,
    public catalog_object_id: string = null,
    public metadata: string = null,
    public name: string = null,
    public reward_ids: string = null
  ) {
  }
}

export class Coupon {
  constructor(
    public code: string = null,
    public id: string = null,
    public type: DiscountType = DiscountType.discount,
    public locationId: string = null
  ) {
  }
}

export class DiscountOrder {
  constructor(
    public id: string = null
  ) {
  }
}

export class DiscountRequest {
  constructor(
    public catalog_object_id: string = null,
    public scope: string = 'ORDER'
  ) {
  }
}

export class Tax {
  constructor(
    public applied_money: Price = null,
    public catalog_object_id: string = null,
    public metadata: string = null,
    public name: string = null,
    public percentage: string = null,
    public scope: string = null,
    public type: string = null,
    public uid: string = null
  ) {
  }
}

export class Merchant {
  constructor(
    public readonly merchantId: string = null,
    public merchantName: string = null,
    public merchantEmail: string = null,
    public merchantHeaderTitle: string = null,
    public merchantHeaderText: string = null,
    public available: boolean = true,
    public merchantBanner: string = null,
    public merchantLogo: string = null,
    public showLocationsMap: boolean = false,
    public milesAwayThreshold: number = null,
    public locationImageType: ImageConfig = ImageConfig.default,
    public locationImageUrl: string = null,
    public categoryFilterEnabled: boolean = false,
    public vanityLink: string = null,
    public useFlavorShiftsTime: boolean = false
  ) {
  }
}

export class Group {
  constructor(
    public readonly groupId: string = null,
    public displayName: string = null,
    public brandColor: string = null,
    public backgroundColor: string = null,
    public borderColor: string = null,
    public primaryTextColor: string = null,
    public secondaryTextColor: string = null,
    public bannerImageUrl: string = null,
    public logoImageUrl: string = null,
    public phoneNumber: string = null,
    public tipAmounts: string = '',
    public tipType: string = null,
    public tippingEnabled: boolean = false,
    public locationsIds: string[] = [],
    public groupHeaderText: string = null,
    public groupHeaderTitle: string = null,
    public address: RestaurantAddress = new RestaurantAddress(),
    public additionalKyooFee: number = null,
    public deliveryAdditionalKyooFee: number = null,
    public tabsAdditionalKyooFee: number = null,
    public kyooFee: number = null,
    public deliveryKyooFee: number = null,
    public tabsKyooFee: number = null,
    public country: string = null,
    public autoGratuityPercent: number = null,
    public sellerDeliveryEnabled: boolean = false,
    public courierDeliveryEnabled: boolean = false,
    public combineGroupLocations: boolean = false,
    public useFlavorShifts: boolean = false,
    public vanityLink: string = null,
    public closeInactiveTabsAfterMinutes?: number,
  ) {
  }
}

export class DailyAvailabilityResponse {
  constructor(
    public enabled: boolean,
    public value: number,
    public start_time: WorkTime,
    public end_time: WorkTime
  ) {}
}

export class Country {
  constructor(
    public name: string = null,
    public currency: string = null,
    public currencySign: string = null,
    public phoneCode: string = null,
    public applicationId: string = null
  ) {
  }

  public static getCountryByName(name: string): Country {
    const US = new Country('US', 'USD', '$', '+1', environment.applicationId);
    const CA = new Country('CA', 'CAD', '$', '+1', environment.applicationIdCA);
    const GB = new Country('GB', 'GBP', '£', '+44', environment.applicationIdGB);
    const AU = new Country('AU', 'AUD', '$', '+61', environment.applicationIdAU);
    const JP = new Country('JP', 'JPY', '¥', '+81', environment.applicationIdJP);
    switch (name) {
      case 'US': return US;
      case 'CA': return CA;
      case 'GB': return GB;
      case 'AU': return AU;
      case 'JP': return JP;
      default:   return US;
    }
  }
}

export class CustomCss {
  constructor(
    public font: string = null,
    public enlargeImages: boolean = false,
    public itemColumns: number = 3
  ) {
  }
}

export class TimeRange {
  constructor(
    public startTime: Date,
    public endTime: Date
  ) {}
}

export class RestaurantAvailableStatus {
  constructor(
    public isSharedCardsEnabled: boolean = null,
    public isAcceptingOrders: boolean = null,
    public isCurbsideEnabled: boolean = null,
    public isTablesideEnabled: boolean = null,
    public isPickupEnabled: boolean = null,
    public isDeliveryEnabled: boolean = null,
    public isXSideEnabled: boolean = null,
    public isFridgeEnabled: boolean = null,
    public isHidden: boolean = null,
    public handoffLocation: string = null,
    public deliverySeller: boolean = false,
    public deliveryCourier: boolean = false,
    public deliverySellerMinAmount: number = 0,
    public deliveryCourierMinAmount: number = 0,
    public nextOpenTimeUtc: string = null,
    public estimatedTimeToBeginPreparation: string = null,
    public hideETA: boolean = null,
    public numPersonsAhead: number = 0,
    public maxOrdersInQueue: number = 0,
    public placeInLine: number = 0,
    public hours: any[] = [],
    public blackoutDateRanges: any[] = [],
    public tipAmounts: number[] = [],
    public tipType: string = null,
    public country: string = null,
    public currency: string = null,
    public customCSS: string = null,
    public loyaltyPointsName: any = null,
    public gift_cards_permissions_requested: boolean = false,
    public default_delivery_auto_gratuity_percent: number = 0,
    public enable_restaurant_tip: boolean = false,
    public enableSoldOutOrders: boolean = false
  ) {
  }
}

export type Coordinate = {
  latitude: number;
  longitude: number;
};

export class RewardTiers {
  constructor(
    public id: string = null,
    public name: string = null,
    public points: string = null,
    public createdAt: string = null,
    public definition: object = null
  ) {}
}

export class LoyaltyProgram {
  constructor (
    public status: string = null,
    public rewardTiers:RewardTiers[] = [],
    public expirationPolicy:ExpirationPolicy,
    public terminology: Terminology,
    public locationIds: string[] = [],
    public accrualRules: AccrualRule[] = []

  ) {}
}

export class ExpirationPolicy {
  constructor(
    public expirationDuration:string =""
  ){}
}
export class Terminology {
  constructor(
    public one:string="",
    public other:string=""
  ) {}
}

export class AmountMoney {
  amount: number;
  currency: string;

  constructor(amount: number, currency: string) {
    this.amount = amount;
    this.currency = currency;
  }
}

export class SpendData {
  amount_money: AmountMoney;
  excluded_category_ids: string[] | null;
  excluded_item_variation_ids: string[] | null;
  tax_mode: string;

  constructor(
    amount_money: AmountMoney,
    excluded_category_ids: string[] | null,
    excluded_item_variation_ids: string[] | null,
    tax_mode: string
  ) {
    this.amount_money = amount_money;
    this.excluded_category_ids = excluded_category_ids;
    this.excluded_item_variation_ids = excluded_item_variation_ids;
    this.tax_mode = tax_mode;
  }
}

export class AccrualRule {
  accrual_type: string;
  points: number;
  visit_data: any | null;
  spend_data: SpendData | null;
  item_variation_data: any | null;
  category_data: any | null;

  constructor(
    accrual_type: string,
    points: number,
    visit_data: any | null,
    spend_data: SpendData | null,
    item_variation_data: any | null,
    category_data: any | null
  ) {
    this.accrual_type = accrual_type;
    this.points = points;
    this.visit_data = visit_data;
    this.spend_data = spend_data;
    this.item_variation_data = item_variation_data;
    this.category_data = category_data;
  }
}

export class SideMenu {
    constructor(
      public id: number = null,
      public title: string = null,
      public hide: boolean = false,
      public disabled: boolean = false,
      public icon:string = null,
    ) {}
}

export class MerchantMobileApp {
  constructor(
    public readonly id: string = null,
    public merchantName: string = null,
    public merchantEmail: string = null,
    public name: string = null,
    public subtitle: string = null,
    public description: string = null,
    public tagline: string = null,
    public homeScreenSummary: string = null,
    public aboutPageUrl: string = null,
    public learnMorePageUrl: string = null,
    public splashScreenImageUrl: string = null,
    public homeScreenImageUrl: string = null,
    public iconUrl: string = null,
    public bannerUrl: string = null,
    public imageUrl: string = null,
    public displayGiftCards: boolean = false,
    public displayPopularItems: boolean = false,
    public displayFrequentlyOrderedItems: boolean = false,
    public displayOurStoreMessage: boolean = false,
    public ourStoreMessage: string = null,
    public isPrivate: boolean = false,
    public displayProfilePhoto: boolean = false,
    public enableDesktop: boolean = false,
    public oneSignalAppId: string = null
  ) {
  }
}

export class GiftCardImage {
  constructor(
    public readonly id: string = null,
    public imageUrl: string = null,
    public defaultImage: boolean = false
  ) {}
}

export class MobileAppHomepageTile {
  constructor(
    public readonly id: string = null,
    public title: string = null,
    public orderNumber: number = 0,
    public description: string = null,
    public imageUrl: string = null,
    public redirectButton: boolean = false,
    public buttonText: string = null,
    public redirectUrl: string = null,
    public productButton: boolean = false,
    public locationId: string = null,
    public categoryId: string = null,
    public itemId: string = null,
    public promoButton: boolean = false,
    public promoImageUrl: string = null,
    public backgroundColor: string = null,
    public navigationPath: string = null,
    public showAsPopup: boolean = false,
    public showAsBanner: boolean = false,
    public discountId: string = null,
    public discountName: string = null,
    public discountTerms: string = null,
    public useCurrentLocation: boolean = false
  ) {}

  // Null when the tile follows the customer into whichever location they are viewing.
  public get pinnedLocationId(): string | null {
    return this.useCurrentLocation ? null : this.locationId || null;
  }

  public get isCatalogNavigation(): boolean {
    return ['category', 'item'].includes((this.navigationPath || '').toLowerCase());
  }
}

export class MobileAppMenuTab {
  constructor(
    public readonly id: string = null,
    public title: string = null,
    public redirect: boolean = false,
    public redirectUrl: string = null,
    public content: string = null
  ) {}
}

export class CatalogItemIcon {
  constructor(
    public readonly id: string =  null,
    public itemId: string = null,
    public merchantId: string = null,
    public svgIconText: string =  null
  ) {}
}

export interface Categories {
  name: string;
  location_ids: string[];
}

export class Notification {
  constructor(
    public readonly id: string = null,
    public title: string = null,
    public message: string = null,
    public scheduledTime: Date = null,
    public notificationId: string = null,
  ){}
}

export class RewardProduct {
  constructor(
    public id: string = '',
    public items: { all_items: boolean; items: string[] } = { all_items: false, items: [] }
  ) {}
}

export class KyooPromo {
  constructor(
    public readonly id: string = null,
    public type: string = null,
    public itemId: string = null,
    public spendPrice: number = null,
    public promoPrice: number = null,
    public isActive: boolean = false
  ){}
}
